import React from 'react';
import { CheckIcon, MonitorIcon, NetworkIcon } from 'lucide-react';
import { ButtonLink } from './Button';
import { Halo, RuleDot } from './Decor';
import { services } from '../data/services';
import { SECTION_IDS } from '../utils/scroll';
const secondaryIcons: Record<string, React.ReactNode> = {
  'presencia-digital': <MonitorIcon className="h-5 w-5" strokeWidth={1.4} aria-hidden="true" data-id="element-263" data-mp="18twgejreb1hl" />,
  'full-creation': <NetworkIcon className="h-5 w-5" strokeWidth={1.4} aria-hidden="true" data-id="element-264" data-mp="13w4m5j3a33a8" />
};
function Includes({
  items,
  dense = false
}: {
  items: string[];
  dense?: boolean;
}) {
  return <ul className={`space-y-2.5 sm:space-y-3 ${dense ? 'text-[0.925rem]' : 'text-[0.95rem]'}`} data-id="element-265" data-mp="u5s5w32hxw9w">
      {items.map(item => <li key={item} className="flex items-start gap-3 text-ink/85" data-id="element-266" data-mp="q5hprxkzpphb">
          <span className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-lavender-300 text-plum-700" data-id="element-267" data-mp="l53qb3xgfnuw">
            <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" data-id="element-268" data-mp="g6sguub2nfd7" />
          </span>
          <span data-id="element-269" data-mp="36awqum9to73e">{item}</span>
        </li>)}
    </ul>;
}
export function Services() {
  const [featured, ...rest] = services;
  return <section id={SECTION_IDS.servicios} aria-labelledby="servicios-titulo" className="relative scroll-mt-24 overflow-hidden bg-ivory sm:scroll-mt-28" data-id="element-270" data-mp="2xc1hx2fe8iwe">
      <Halo soft className="-right-40 top-24 h-[30rem] w-[30rem]" data-id="element-271" data-mp="328rz5t2xpkje" />

      <div className="relative mx-auto max-w-content px-5 py-12 sm:px-8 sm:py-16 lg:py-24" data-id="element-272" data-mp="3q1rxlfwobsum">
        <p className="eyebrow flex items-center gap-3 text-plum-600" data-id="element-273" data-mp="3u0r0kuyuhfjf">
          <RuleDot className="hidden sm:inline-flex" data-id="element-274" data-mp="3g2smzrscln4z" />
          Servicios
        </p>
        <h2 id="servicios-titulo" className="mt-4 max-w-2xl font-display text-[1.7rem] leading-[1.16] text-plum-900 sm:mt-5 sm:text-4xl lg:text-[2.6rem]" data-id="element-275" data-mp="3l13x83mk1znq">
          Elige por dónde comenzar.
        </h2>

        {/* Servicio principal */}
        <article className="relative mt-8 overflow-hidden rounded-xl2 border border-lavender-200 bg-gradient-to-br from-ivory via-ivory to-lavender-50 shadow-lift sm:mt-12" data-id="element-276" data-mp="29k46hcfuvsvt">
          <Halo className="-right-24 -top-24 h-72 w-72" data-id="element-277" data-mp="1ljsecw2hfyxl" />

          <div className="relative grid gap-7 p-6 sm:gap-10 sm:p-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:gap-14 lg:p-12" data-id="element-278" data-mp="1griu9wo1tt38">
            <div className="flex flex-col" data-id="element-279" data-mp="1brwfhisko4sb">
              <div className="flex flex-wrap items-center gap-3" data-id="element-280" data-mp="1bqv1b5g9f26d">
                <p className="eyebrow text-plum-600" data-id="element-281" data-mp="16v60kysi7f8h">{featured.label}</p>
                <span className="eyebrow rounded-pill bg-plum-900 px-3 py-1.5 text-ivory" data-id="element-282" data-mp="11tgnzepmutia">
                  Empieza aquí
                </span>
              </div>

              <h3 className="mt-4 font-display text-[1.7rem] leading-tight text-plum-900 sm:text-[2.3rem]" data-id="element-283" data-mp="sttncv4q9rqa">
                {featured.name}
              </h3>
              <p className="mt-3 max-w-md text-[0.975rem] leading-relaxed text-muted sm:mt-4 sm:text-[1rem]" data-id="element-284" data-mp="2s6fmfffj4yet">
                {featured.supporting}
              </p>

              <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-lavender-200/80 pt-5 sm:mt-7 sm:pt-6" data-id="element-285" data-mp="2us5f823v888r">
                <p className="font-display text-[1.6rem] text-plum-900 sm:text-3xl" data-id="element-286" data-mp="2zom4x0qpquny">
                  {featured.price}
                </p>
                <p className="text-sm text-muted" data-id="element-287" data-mp="39g0bfsh30uql">{featured.priceNote}</p>
              </div>

              <div className="mt-7 sm:mt-8 lg:mt-auto lg:pt-8" data-id="element-288" data-mp="2bshtps5g0iag">
                <ButtonLink size="lg" analyticsId={featured.analyticsId} href={featured.href} className="w-full sm:w-auto" data-id="element-289" data-mp="2bucbixlf3uab">
                  {featured.cta}
                </ButtonLink>
              </div>
            </div>

            <div className="rounded-card border border-lavender-200/80 bg-lavender-50/80 p-5 backdrop-blur-[2px] sm:p-8" data-id="element-290" data-mp="2pq6c8tc1fdcc">
              <p className="eyebrow text-plum-600" data-id="element-291" data-mp="51s1yiclt3bt">Incluye</p>
              <div className="mt-4 sm:mt-5" data-id="element-292" data-mp="508m2suv0uak">
                <Includes items={featured.includes} data-id="element-293" data-mp="3kcw5djzcvme" />
              </div>
            </div>
          </div>
        </article>

        {/* Servicios complementarios */}
        <div className="mt-4 grid gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-2" data-id="element-294" data-mp="3utacxwm1bi71">
          {rest.map(service => <article key={service.id} className="group flex flex-col rounded-xl2 border border-lavender-200 bg-ivory p-5 shadow-hair transition-[box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-1 hover:border-lavender-300 hover:shadow-float sm:p-8" data-id="element-295" data-mp="8jz9ro3q0dvq">
              <div className="flex items-center justify-between gap-4" data-id="element-296" data-mp="wa3bfpxcm3k3">
                <p className="eyebrow text-plum-600" data-id="element-297" data-mp="wbeji1xttxh0">{service.label}</p>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-pill border border-lavender-200 bg-lavender-50 text-plum-700 transition-colors duration-200 ease-premium group-hover:border-lavender-300" data-id="element-298" data-mp="rb8w4xylax4q">
                  {secondaryIcons[service.id]}
                </span>
              </div>

              <h3 className="mt-4 font-display text-xl leading-tight text-plum-900 sm:text-2xl" data-id="element-299" data-mp="1jv4awkecwqvl">
                {service.name}
              </h3>
              <p className="mt-2.5 text-[0.95rem] leading-relaxed text-muted sm:mt-3" data-id="element-300" data-mp="1av7flzxgotrk">
                {service.supporting}
              </p>

              <div className="mt-4 border-t border-lavender-200/80 pt-4 sm:mt-5 sm:pt-5" data-id="element-301" data-mp="1fxgaj1twa5wb">
                <Includes items={service.includes} dense data-id="element-302" data-mp="1154q49pjtxxz" />
              </div>

              <div className="mt-6 sm:mt-7 lg:mt-auto lg:pt-7" data-id="element-303" data-mp="15xxtat5dcy7y">
                <ButtonLink variant="outline" analyticsId={service.analyticsId} href={service.href} className="w-full sm:w-auto" data-id="element-304" data-mp="s6aw8r3lrlo4">
                  {service.cta}
                </ButtonLink>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}